import OpenInNew from "@mui/icons-material/OpenInNew";
import { IconButton, Tooltip } from "@mui/material";
import { GridColDef, GridRowId } from "@mui/x-data-grid-premium";
import { Dialog } from "components/Dialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SubmissionMaterializationType,
  SubmissionStatus,
  SubmissionType,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { WASTE_SUBMISSIONS_FOR_STAFF_PICKER_QUERY } from "routes/Staff/WasteFulfillment/api";

import { mapWasteSubmissionRowsToCommunicationSubmissions } from "../hooks/useCommunicationSubmissions";
import { CommunicationSubmission } from "../types";

interface SubmissionsPickerDialogProps {
  tenantId: string;
  initialSubmissionIds: string[];
  close: () => void;
  saveSubmissions: (submissions: CommunicationSubmission[]) => void;
}

type WasteRow = {
  id: string;
  submissionId?: string | null;
  title: string | null;
  submissionType?: string | null;
  submissionStatus: string;
  tenantId: string;
};

// Exclude completed statuses (match Waste Fulfillment grid default)
const PICKER_STATUS_SEARCH =
  "status:NOT_STARTED|IN_PROGRESS|ENCAMP_INITIAL_REVIEW|IN_REVIEW|PROCESSING_SUBMISSION";

// Same 90-day window as Waste Fulfillment grid (start of today → end of day 90 days out)
function getPickerDateRange() {
  const startOfToday = DateTime.now().startOf("day");
  const endOfNinetyDays = startOfToday.plus({ days: 90 }).endOf("day");
  return {
    startDate: startOfToday.toISO() ?? "",
    endDate: endOfNinetyDays.toISO() ?? "",
  };
}

export const SubmissionsPickerDialog = ({
  tenantId,
  initialSubmissionIds,
  close,
  saveSubmissions,
}: SubmissionsPickerDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSubmissionIds);
  const [selectedRows, setSelectedRows] = useState<WasteRow[]>([]);

  const pickerDateRange = useMemo(() => getPickerDateRange(), []);

  const dataQueryVariables = useMemo(
    () =>
      tenantId
        ? {
            materializationTypes: [
              SubmissionMaterializationType.OneOff,
              SubmissionMaterializationType.Materialized,
            ],
            search: `tenantId:${tenantId} ${PICKER_STATUS_SEARCH}`,
            includeOverdue: true,
            onlyOverdue: false,
            startDate: pickerDateRange.startDate,
            endDate: pickerDateRange.endDate,
            sort: [{ field: "deadline", sort: "asc" }],
          }
        : undefined,
    [tenantId, pickerDateRange.startDate, pickerDateRange.endDate]
  );

  const getItems = useCallback(
    (data: { wasteSubmissionsForStaffPicker: { items: WasteRow[] } }) => {
      const rows = data.wasteSubmissionsForStaffPicker.items as WasteRow[];
      return rows.map((row) => ({
        ...row,
        id: row.submissionId ?? row.id,
      }));
    },
    []
  );

  const getCount = useCallback(
    (data: { wasteSubmissionsForStaffPicker: { count: number } }) =>
      data.wasteSubmissionsForStaffPicker.count,
    []
  );

  const handleSelectedRowsChanged = useCallback((rows: WasteRow[]) => {
    setSelectedRows(rows);
  }, []);

  const handleSelectedIdsChanged = useCallback((ids: GridRowId[]) => {
    setSelectedIds(ids.map(String));
  }, []);

  const columns: GridColDef<WasteRow>[] = useMemo(
    () => [
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
        minWidth: 200,
      },
      {
        field: "submissionType",
        headerName: "Type",
        flex: 0.8,
        minWidth: 120,
        valueFormatter: (value) => (value != null ? prettyPrintEnumValue(String(value)) : ""),
      },
      {
        field: "submissionStatus",
        headerName: "Status",
        flex: 0.8,
        minWidth: 120,
        valueFormatter: (value) => (value != null ? prettyPrintEnumValue(String(value)) : ""),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        minWidth: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const submissionId = params.row.submissionId;
          if (!submissionId) return null;
          return (
            <Tooltip title="View submission">
              <Link
                to={`/staff/fulfillment/waste/submissions/${submissionId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                  <OpenInNew fontSize="small" />
                </IconButton>
              </Link>
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const handleDone = () => {
    const rowById = new Map(selectedRows.map((r) => [r.submissionId ?? r.id, r]));
    const submissions: CommunicationSubmission[] = selectedIds.map((id) => {
      const row = rowById.get(id);
      if (row) {
        const mapped = mapWasteSubmissionRowsToCommunicationSubmissions([row]);
        if (mapped[0]) return mapped[0];
      }
      return {
        id,
        title: "",
        submissionType: SubmissionType.AnnualReport,
        status: SubmissionStatus.NotStarted,
      };
    });
    saveSubmissions(submissions);
    close();
  };

  return (
    <Dialog
      open
      maxWidth="lg"
      onClose={close}
      title="Associate Submissions"
      description="Select waste submissions to associate with this communication. Only materialized submissions (one-off or from a series) are shown."
      primaryActionButtonProps={{
        onClick: handleDone,
        saveText: "Done",
        hideStartIcon: true,
        loading: false,
      }}
    >
      <OmnisearchDataGrid
        tenantId={tenantId}
        persistenceKey="communications-submissions-picker"
        columns={columns}
        dataQuery={WASTE_SUBMISSIONS_FOR_STAFF_PICKER_QUERY}
        skip={!tenantId}
        dataQueryVariables={dataQueryVariables}
        initialSortModel={[{ field: "deadline", sort: "asc" }]}
        getItems={getItems}
        getCount={getCount}
        noDataMessage="No submissions found for this tenant."
        isURLDriven={false}
        selectedIds={selectedIds}
        disableRowSelectionOnClick={false}
        withPadding={false}
        initialPageSize={10}
        onSelectedIdsChanged={handleSelectedIdsChanged}
        onSelectedRowsChanged={handleSelectedRowsChanged}
        showManageColumns={false}
      />
    </Dialog>
  );
};
